import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import {
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Globe,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import teamGranularService from '@/services/teamGranularService'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'

function formatDate(d) {
  if (!d) return ''
  try {
    return new Date(d).toLocaleDateString('es-419', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

function CenterShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8 pt-safe pb-safe">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

export default function TeamInvite() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { user, isAuthenticated, refreshSession } = useAuthStore()

  const [loading, setLoading] = useState(true)
  const [invite, setInvite] = useState(null)
  const [error, setError] = useState(null)
  const [errorCode, setErrorCode] = useState(null)

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const response = await teamGranularService.resolveByToken(token)
        if (!cancelled) {
          setInvite(response?.data || response)
          setError(null)
          setErrorCode(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'No se pudo cargar la invitación')
          setErrorCode(err.response?.data?.code || 'INVITE_ERROR')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleAccept = async (payload = {}) => {
    setSubmitting(true)
    try {
      const response = await teamGranularService.acceptInvite(token, payload)
      const data = response?.data || response
      if (data?.createdUser) {
        toast.success('Cuenta creada. Inicia sesión para entrar al Country Hub.')
        navigate(`/login?email=${encodeURIComponent(data.email)}&welcome=team`)
        return
      }
      if (isAuthenticated) {
        await refreshSession()
        toast.success('¡Bienvenido al equipo!')
        navigate('/?welcome=team')
        return
      }
      toast.success('Invitación aceptada. Inicia sesión.')
      navigate(`/login?email=${encodeURIComponent(invite?.member?.email || '')}`)
    } catch (err) {
      const code = err.response?.data?.code
      if (code === 'NEEDS_LOGIN') {
        const email = err.response?.data?.email || invite?.member?.email || ''
        navigate(
          `/login?email=${encodeURIComponent(email)}&next=${encodeURIComponent(`/team-invite/${token}`)}`
        )
        return
      }
      toast.error(err.response?.data?.message || 'Error al aceptar la invitación')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateAccount = (e) => {
    e.preventDefault()
    if (password.length < 8) return toast.error('Mínimo 8 caracteres')
    if (password !== confirmPassword) return toast.error('Las contraseñas no coinciden')
    if (!firstName.trim() || !lastName.trim())
      return toast.error('Ingresa tu nombre y apellido')
    handleAccept({
      password,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    })
  }

  if (loading) {
    return (
      <CenterShell>
        <Card className="p-8 text-center">
          <Loader2 size={28} className="mx-auto animate-spin text-primary mb-3" />
          <p className="text-body-sm text-subtle">Cargando invitación…</p>
        </Card>
      </CenterShell>
    )
  }

  if (error) {
    const isExpired = errorCode === 'INVITE_EXPIRED' || errorCode === 'INVITE_REVOKED'
    return (
      <CenterShell>
        <Card className="p-6 sm:p-8 text-center">
          <XCircle size={40} className="mx-auto text-danger mb-3" />
          <h1 className="font-display text-h2 font-semibold text-foreground">
            {isExpired ? 'Invitación expirada' : 'Invitación no disponible'}
          </h1>
          <p className="text-body-sm text-subtle mt-2 mb-4">{error}</p>
          {isExpired && (
            <Alert variant="info" className="text-left mb-4">
              <AlertDescription>
                Pide al admin del Country Hub que te reenvíe una nueva invitación.
              </AlertDescription>
            </Alert>
          )}
          <Button asChild variant="outline">
            <RouterLink to="/login">Ir al inicio</RouterLink>
          </Button>
        </Card>
      </CenterShell>
    )
  }

  if (!invite) return null

  const { partner, member, permissionsLabels, userExists, alreadyAccepted } = invite
  const presetLabel = member?.preset?.label
  const country = partner?.managedCountry || ''
  const orgName = partner?.organizationName || 'Country Hub'

  if (alreadyAccepted) {
    return (
      <CenterShell>
        <Card className="p-6 sm:p-8 text-center">
          <CheckCircle2 size={40} className="mx-auto text-success mb-3" />
          <h1 className="font-display text-h2 font-semibold text-foreground">
            Esta invitación ya fue aceptada
          </h1>
          <p className="text-body-sm text-subtle mt-2 mb-4">
            Inicia sesión con <strong>{member.email}</strong> para entrar al Country Hub.
          </p>
          <Button asChild size="cta">
            <RouterLink to={`/login?email=${encodeURIComponent(member.email)}`}>
              Iniciar sesión
            </RouterLink>
          </Button>
        </Card>
      </CenterShell>
    )
  }

  if (
    isAuthenticated &&
    user?.email &&
    user.email.toLowerCase() !== member.email.toLowerCase()
  ) {
    return (
      <CenterShell>
        <Card className="p-6 sm:p-8">
          <Alert variant="warning" className="mb-4">
            <AlertDescription>
              Esta invitación es para <strong>{member.email}</strong>, pero estás logueado
              como <strong>{user.email}</strong>. Cierra sesión y vuelve a abrir el enlace.
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            size="cta"
            onClick={() => {
              localStorage.removeItem('partnerToken')
              localStorage.removeItem('partnerRefreshToken')
              localStorage.removeItem('partnerUser')
              window.location.reload()
            }}
          >
            Cerrar sesión
          </Button>
        </Card>
      </CenterShell>
    )
  }

  const inviteHeader = (
    <div className="mb-4">
      <div className="flex flex-wrap gap-2 mb-2">
        <Badge variant="default" className="gap-1">
          <Globe size={11} /> Country Hub{country ? ` · ${country}` : ''}
        </Badge>
        {presetLabel && <Badge variant="outline">{presetLabel}</Badge>}
      </div>
      <h1 className="font-display text-h2 font-semibold text-foreground leading-tight">
        {orgName} te invita al equipo
      </h1>
      {member.invitedBy?.name && (
        <p className="text-body-sm text-subtle mt-1">
          Invitado por <strong>{member.invitedBy.name}</strong>
          {member.invitedBy.email ? ` (${member.invitedBy.email})` : ''}
        </p>
      )}
      {member.expiresAt && (
        <div className="flex items-center gap-1.5 mt-2 text-caption text-subtle">
          <Clock size={12} /> Expira el {formatDate(member.expiresAt)}
        </div>
      )}
    </div>
  )

  const permissionsBox = permissionsLabels && (
    <Card className="p-4 mb-4 border-line">
      <p className="text-body-sm font-semibold text-foreground mb-2">Lo que podrás hacer</p>
      {permissionsLabels.can.length === 0 ? (
        <p className="text-body-sm text-subtle">— Solo lectura —</p>
      ) : (
        <ul className="space-y-1">
          {permissionsLabels.can.map((p) => (
            <li key={p.key} className="flex items-start gap-2 text-body-sm">
              <CheckCircle2 size={14} className="text-success mt-0.5 shrink-0" />
              {p.label}
            </li>
          ))}
        </ul>
      )}

      {permissionsLabels.cannot.length > 0 && (
        <>
          <Separator className="my-3" />
          <p className="text-body-sm font-semibold text-subtle mb-2">No incluido</p>
          <ul className="space-y-1">
            {permissionsLabels.cannot.map((p) => (
              <li
                key={p.key}
                className="flex items-start gap-2 text-body-sm text-subtle"
              >
                <XCircle size={14} className="text-neutral-400 dark:text-neutral-500 mt-0.5 shrink-0" />
                {p.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  )

  if (!userExists) {
    return (
      <CenterShell>
        <Card className="p-6 sm:p-8">
          {inviteHeader}
          <div className="flex items-center gap-1.5 mb-3 text-body-sm text-foreground">
            <Mail size={14} className="text-subtle" /> Tu email:{' '}
            <strong>{member.email}</strong>
          </div>
          {permissionsBox}

          <Alert variant="info" className="mb-4">
            <AlertDescription>
              Crea una cuenta con esta dirección. Después podrás iniciar sesión cuando
              quieras.
            </AlertDescription>
          </Alert>

          <form onSubmit={handleCreateAccount} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ti-fname">Nombre</Label>
                <Input
                  id="ti-fname"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ti-lname">Apellido</Label>
                <Input
                  id="ti-lname"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ti-pass">Contraseña</Label>
              <Input
                id="ti-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p className="text-caption text-subtle">Mínimo 8 caracteres</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ti-confirm">Confirmar contraseña</Label>
              <Input
                id="ti-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" size="cta" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Creando cuenta…
                </>
              ) : (
                'Crear cuenta y entrar'
              )}
            </Button>
          </form>
        </Card>
      </CenterShell>
    )
  }

  if (!isAuthenticated) {
    return (
      <CenterShell>
        <Card className="p-6 sm:p-8">
          {inviteHeader}
          {permissionsBox}
          <Alert variant="info" className="mb-4">
            <AlertDescription>
              Ya tienes cuenta con <strong>{member.email}</strong>. Inicia sesión para
              confirmar el acceso al Country Hub.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Button asChild size="cta">
              <RouterLink
                to={`/login?email=${encodeURIComponent(member.email)}&next=${encodeURIComponent(`/team-invite/${token}`)}`}
              >
                Iniciar sesión
              </RouterLink>
            </Button>
            <Button asChild variant="link" size="sm">
              <RouterLink
                to={`/forgot-password?email=${encodeURIComponent(member.email)}`}
              >
                Olvidé mi contraseña
              </RouterLink>
            </Button>
          </div>
        </Card>
      </CenterShell>
    )
  }

  return (
    <CenterShell>
      <Card className="p-6 sm:p-8">
        {inviteHeader}
        {permissionsBox}
        <Alert variant="success" className="mb-4">
          <AlertDescription>
            Estás logueado como <strong>{user.email}</strong>. Confirma para añadir este
            Country Hub a tus accesos.
          </AlertDescription>
        </Alert>
        <Button onClick={() => handleAccept()} disabled={submitting} size="cta">
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Confirmando…
            </>
          ) : (
            'Aceptar y entrar al Country Hub'
          )}
        </Button>
      </Card>
    </CenterShell>
  )
}
